import { useEffect, useState } from 'react';
import { MsComboBox } from './MsComboBox';
import { AlertBlock } from './AlertBlock';
import { useTagSuggestions, useTagsByIds, type Tag } from '../hooks/useTags';

/**
 * TagFilterBuilder — the React port of the Angular iio-tag-boolean / resourceFilter
 * builder. Three tag buckets (And / Or / Not) with typeahead, editing a draft filter;
 * Apply commits it, Undo reverts to the applied filter, Clear empties it. Mirrors the
 * Angular contract: the stored/queried filter carries tag *ids* (TagFilter), while the
 * buckets edit hydrated Tag objects.
 */

/** Stored/queried filter shape (tag ids), matching PreferenceService.CurrentFilter. */
export interface TagFilter {
  projectId: string;
  size: number;
  tags: { and: string[]; or: string[]; not: string[] };
}

type Buckets = { and: Tag[]; or: Tag[]; not: Tag[] };
type BucketKey = keyof Buckets;

const EMPTY_BUCKETS: Buckets = { and: [], or: [], not: [] };

export function emptyTagFilter(projectId: string): TagFilter {
  return { projectId, size: 0, tags: { and: [], or: [], not: [] } };
}

/**
 * Coerces whatever the filter cookie holds into a usable TagFilter, or null.
 *
 * The cookie is shared with Angular and long-lived, so it can hold a filter with missing
 * buckets, a stale shape, or one belonging to a different project. Angular's RestoreFilter
 * guarded on `size > 0` and a matching projectId before touching `tags`; without the same
 * guard a partial object reaches the builder and throws on `tags.and`.
 */
export function normalizeTagFilter(raw: unknown, projectId: string): TagFilter | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Partial<TagFilter>;
  if (f.projectId !== projectId) return null;
  const ids = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  const tags = {
    and: ids(f.tags?.and),
    or: ids(f.tags?.or),
    not: ids(f.tags?.not),
  };
  const size = tags.and.length + tags.or.length + tags.not.length;
  if (size === 0) return null;
  return { projectId, size, tags };
}

function bucketsToFilter(projectId: string, b: Buckets): TagFilter {
  const ids = (tags: Tag[]) => tags.map((t) => t.id);
  const tags = { and: ids(b.and), or: ids(b.or), not: ids(b.not) };
  return { projectId, size: tags.and.length + tags.or.length + tags.not.length, tags };
}

const idSet = (b: Buckets) =>
  JSON.stringify({ and: b.and.map((t) => t.id), or: b.or.map((t) => t.id), not: b.not.map((t) => t.id) });

interface Props {
  projectId: string;
  /** The currently-applied (id-based) filter, or null for none. */
  appliedFilter: TagFilter | null;
  onApply: (filter: TagFilter) => void;
}

export function TagFilterBuilder({ projectId, appliedFilter, onApply }: Props) {
  // Hydrate the applied filter's ids into Tag objects (name/color) for the chips —
  // mirrors Angular BuildFilterFromCookie's tagService.FindMany call.
  // Normalize defensively: this component is also reachable with a filter straight from
  // the shared cookie, which is not guaranteed to carry all three buckets.
  const applied = normalizeTagFilter(appliedFilter, projectId);
  const appliedIds = applied
    ? [...applied.tags.and, ...applied.tags.or, ...applied.tags.not]
    : [];
  const { data: hydratedTags } = useTagsByIds(projectId, appliedIds);

  const [draft, setDraft] = useState<Buckets>(EMPTY_BUCKETS);

  // Reset the draft to the applied filter whenever it (or its hydrated tags) changes.
  useEffect(() => {
    if (!applied) {
      setDraft(EMPTY_BUCKETS);
      return;
    }
    if (!hydratedTags) return; // wait for hydration
    const byId = new Map(hydratedTags.map((t) => [t.id, t]));
    const pick = (ids: string[]) => ids.map((id) => byId.get(id)).filter((t): t is Tag => !!t);
    setDraft({
      and: pick(applied.tags.and),
      or: pick(applied.tags.or),
      not: pick(applied.tags.not),
    });
    // `applied` is rebuilt each render; key the effect on its contents, not its identity.
  }, [JSON.stringify(applied), hydratedTags]);

  const appliedBuckets: Buckets = (() => {
    if (!applied || !hydratedTags) return EMPTY_BUCKETS;
    const byId = new Map(hydratedTags.map((t) => [t.id, t]));
    const pick = (ids: string[]) => ids.map((id) => byId.get(id)).filter((t): t is Tag => !!t);
    return { and: pick(applied.tags.and), or: pick(applied.tags.or), not: pick(applied.tags.not) };
  })();

  const dirty = idSet(draft) !== idSet(appliedBuckets);
  const draftSize = draft.and.length + draft.or.length + draft.not.length;

  const setBucket = (key: BucketKey, tags: Tag[]) => setDraft((d) => ({ ...d, [key]: tags }));

  return (
    <div className="well tag-filter-panel" data-testid="tag-filter-panel">
      <div className="row">
        <div className="col-md-6">
          <TagBucket title="And Tags" testId="tag-filter-and-select" projectId={projectId} selected={draft.and} onChange={(t) => setBucket('and', t)} />
          <TagBucket title="Or Tags" testId="tag-filter-or-select" projectId={projectId} selected={draft.or} onChange={(t) => setBucket('or', t)} />
          <TagBucket title="Not Tags" testId="tag-filter-not-select" projectId={projectId} selected={draft.not} onChange={(t) => setBucket('not', t)} />
        </div>
        <div className="col-md-6">
          {draftSize > 0 && (
            <button type="button" className="btn btn-default" onClick={() => setDraft(EMPTY_BUCKETS)} data-testid="tag-filter-clear-btn">
              <i className="fas fa-trash" /> Clear Filter
            </button>
          )}
          {dirty && (
            <AlertBlock type="warning" heading="Warning!" testId="tag-filter-unapplied-panel">
              <p>This filter has not yet been applied to your current view</p>
              <p>
                <button type="button" className="btn btn-default" onClick={() => setDraft(appliedBuckets)} data-testid="tag-filter-undo-btn">
                  <i className="fas fa-undo" /> Undo
                </button>{' '}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => onApply(bucketsToFilter(projectId, draft))}
                  data-testid="tag-filter-apply-btn"
                >
                  <i className="fas fa-tags" /> Apply Filter
                </button>
              </p>
            </AlertBlock>
          )}
        </div>
      </div>
    </div>
  );
}

// One And/Or/Not bucket: a tag multi-select with server-side typeahead.
function TagBucket({
  title,
  projectId,
  selected,
  onChange,
  testId,
}: {
  title: string;
  projectId: string;
  selected: Tag[];
  onChange: (tags: Tag[]) => void;
  testId: string;
}) {
  const [query, setQuery] = useState('');
  const { data: suggestions } = useTagSuggestions(projectId, query);

  return (
    <div className="tag-boolean-item">
      <div className="well well-small" style={{ backgroundColor: '#fff' }}>
        <span className="label label-default">{title}</span>
        <br />
        <br />
        <MsComboBox<Tag>
          selected={selected}
          onChange={onChange}
          getKey={(t) => t.id}
          getLabel={(t) => t.name}
          getColor={(t) => t.color}
          icon="fa-circle"
          suggestions={suggestions ?? []}
          onQueryChange={setQuery}
          testId={testId}
        />
      </div>
    </div>
  );
}
